import React from 'react';
import { useData } from '../hooks/useData.js';
import { LoadingSpinner, Button, PageHeader } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';

const AssetDetailPage = ({ token, assetId, assetType, onBack }) => {
    const endpoint = `/business-apps/${assetType === 'bundle' ? 'bundles' : 'contents'}/${assetId}`;
    const { data: asset, isLoading, error } = useData(endpoint, token);

    if (isLoading) return <LoadingSpinner />;
    if (error) return <div className="text-red-500 p-4 bg-red-50 rounded-md">Error: {error.message}</div>;
    if (!asset) return <div>Asset not found.</div>;

    const isBundle = assetType === 'bundle';

    return (
        <div className="space-y-6 p-6">
            <div className="flex items-center gap-4">
                <Button onClick={onBack} variant="secondary">
                    <DynamicIcon name="ChevronLeft" className="w-5 h-5 mr-2" />
                    Back to Library
                </Button>
                <PageHeader title={asset.name} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-start gap-6">
                    <DynamicIcon name={isBundle ? (asset.icon || 'Package') : 'BrainCircuit'} className="w-16 h-16 text-primary flex-shrink-0" />
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{asset.name} <span className="text-lg font-normal text-gray-500">v{asset.version}</span></h2>
                        <p className="text-gray-600 dark:text-gray-400 mt-2">{asset.description}</p>
                        <div className="mt-4 flex items-center gap-6 text-sm">
                            <div className="flex items-center gap-2">
                                <DynamicIcon name="Component" className="w-4 h-4 text-gray-500" />
                                <span className="font-medium">{isBundle ? 'App Bundle' : asset.content_type}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <DynamicIcon name="CheckCircle" className="w-4 h-4 text-green-500" />
                                <span className="font-medium text-green-600 dark:text-green-400">{asset.status}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isBundle && asset.contents?.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-bold mb-4">Included Content</h3>
                    <div className="space-y-3">
                        {asset.contents.map(content => (
                            <div key={content.id} className="p-3 border dark:border-gray-700 rounded-md flex items-center gap-4">
                                <DynamicIcon name="BrainCircuit" className="w-6 h-6 text-primary/70" />
                                <div>
                                    <p className="font-semibold">{content.name}</p>
                                    <p className="text-xs text-gray-500">{content.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!isBundle && asset.connections?.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-bold mb-4">Required Connections</h3>
                     <div className="space-y-3">
                        {asset.connections.map(conn => (
                            <div key={conn.id} className="p-3 border dark:border-gray-700 rounded-md flex items-center gap-4">
                                <DynamicIcon name={conn.category === 'Database' ? 'Database' : (conn.category === 'LLM' ? 'Bot' : 'Share2')} className="w-6 h-6 text-primary/70" />
                                <div>
                                    <p className="font-semibold">{conn.name}</p>
                                    <p className="text-xs text-gray-500">{conn.provider} ({conn.category})</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssetDetailPage;